
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Edit, 
  Plus, 
  Search, 
  Trash, 
  Loader2,
  AlertCircle,
  Code,
  Database,
  Server,
  Smartphone,
  Cloud,
  Shield
} from "lucide-react";
import { api } from "@/lib/api";
import { FormDialog } from "@/components/admin/FormDialog";
import { ServiceForm, ServiceFormValues } from "@/components/admin/ServiceForm";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const iconMap: Record<string, React.ReactNode> = {
  code: <Code className="h-4 w-4" />,
  database: <Database className="h-4 w-4" />,
  server: <Server className="h-4 w-4" />,
  smartphone: <Smartphone className="h-4 w-4" />,
  cloud: <Cloud className="h-4 w-4" />,
  shield: <Shield className="h-4 w-4" />
};

const ServicesAdmin = () => {
  const [search, setSearch] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  const [deletingServiceId, setDeletingServiceId] = useState<string | null>(null);
  
  const queryClient = useQueryClient();
  
  const { 
    data: services = [], 
    isLoading, 
    isError 
  } = useQuery({
    queryKey: ['services'],
    queryFn: api.services.getAll
  });
  
  const filteredServices = services.filter((service: any) =>
    service.name.toLowerCase().includes(search.toLowerCase())
  );

  const getIconComponent = (iconName: string) => {
    return iconMap[iconName.toLowerCase()] || <Code className="h-4 w-4" />;
  };

  const handleCreateService = async (values: ServiceFormValues) => {
    await api.services.create(values);
    setIsCreateDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ['services'] });
  };

  const handleUpdateService = async (values: ServiceFormValues) => {
    if (editingService) {
      await api.services.update(editingService.id, values);
      setEditingService(null);
      queryClient.invalidateQueries({ queryKey: ['services'] });
    }
  };

  const handleDeleteService = async () => {
    if (deletingServiceId) {
      await api.services.delete(deletingServiceId);
      setDeletingServiceId(null);
      queryClient.invalidateQueries({ queryKey: ['services'] });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Services</h1>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Service
        </Button>
      </div>
      
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search services..."
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="flex justify-center items-center py-8 text-destructive">
          <AlertCircle className="h-8 w-8 mr-2" />
          <p>Error loading services. Please try again.</p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Icon</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredServices.length > 0 ? (
                filteredServices.map((service: any) => (
                  <TableRow key={service.id}>
                    <TableCell>
                      <div className="bg-muted w-8 h-8 rounded-full flex items-center justify-center">
                        {service.icon ? getIconComponent(service.icon) : 
                          <Code className="h-4 w-4" />}
                      </div>
                    </TableCell>
                    <TableCell>{service.name}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        service.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {service.status === 'active' ? 'Active' : 'Draft'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setEditingService(service)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setDeletingServiceId(service.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No services found. Try adjusting your search or create a new service.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      
      <FormDialog
        title="Create Service"
        description="Add a new service to your catalog"
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={() => {}}
      >
        <ServiceForm onSubmit={handleCreateService} />
      </FormDialog>
      
      <FormDialog
        title="Edit Service"
        description="Update service information"
        isOpen={!!editingService}
        onClose={() => setEditingService(null)}
        onSubmit={() => {}}
      >
        {editingService && (
          <ServiceForm 
            defaultValues={editingService} 
            onSubmit={handleUpdateService} 
          />
        )}
      </FormDialog>
      
      <AlertDialog open={!!deletingServiceId} onOpenChange={() => setDeletingServiceId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the service.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteService}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ServicesAdmin;
